import { Box } from '@mui/material';
import CasChip from '../../CasChip/CasChip';
import { filterChipLabel } from './advancedFilters';
import { filterLabel } from './renderFilter';

interface ICasFilterChips {
  // Already narrowed to the applied (active) advanced filters by the toolbar.
  filters: any[];
  values: Record<string, any>;
  onClear: (name: string) => void;
  // Opens the drawer on the clicked chip's own control.
  onOpen: (name: string) => void;
}

/*
  Chip text for one applied filter. Filters backed by a select component render
  that component in `mode="display"`, which resolves ids to names using the
  component's OWN option list (already cached from the drawer) — so a tag filter
  reads "Tags: Hot, New" rather than a count or raw ids. Everything else falls
  back to the value-shape formatter.
*/
const ChipLabel = ({ filter, value }: { filter: any; value: any }) => {
  const Component = filter.component;
  if (!Component) return <>{filterChipLabel(filter, value)}</>;

  /*
    Only forward option props the filter config actually defines. Wrapper
    components (e.g. CasTagMultiSelect) fetch their own options and spread
    caller props last, so passing `options={undefined}` would clobber the
    fetched list and crash.
  */
  const optionProps: Record<string, any> = {};
  if (filter.options !== undefined) optionProps.options = filter.options;
  if (filter.optionsIdField !== undefined) optionProps.optionsIdField = filter.optionsIdField;
  if (filter.optionsNameField !== undefined) optionProps.optionsNameField = filter.optionsNameField;

  return (
    <>
      {filterLabel(filter)}:{' '}
      <Component
        mode="display"
        name={filter.name}
        value={value}
        {...optionProps}
        {...(filter.componentProps ?? {})}
      />
    </>
  );
};

// Removable chips for the currently-applied advanced filters.
const CasFilterChips = ({ filters, values, onClear, onOpen }: ICasFilterChips) => {
  if (!filters.length) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
      {filters.map((f) => (
        <CasChip
          key={f.name}
          size="small"
          color="lightGreen"
          label={<ChipLabel filter={f} value={values[f.name]} />}
          onClick={() => onOpen(f.name)}
          onDelete={() => onClear(f.name)}
        />
      ))}
    </Box>
  );
};

export default CasFilterChips;
